import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { API_ENDPOINT } from '../constants';

interface UserAccounts {
  minecraft: string;
  discord: string;
  youtube: string;
  twitch: string;
  twitter: string;
  reddit: string;
}

@customElement('account-form')
export class AccountForm extends LitElement {
  @state() private accounts: UserAccounts = {
    minecraft: '',
    discord: '',
    youtube: '',
    twitch: '',
    twitter: '',
    reddit: ''
  };

  @state() private loading = false;
  @state() private message = '';
  @state() private userId = '';

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        this.userId = user.uid;
        this.loadUserAccounts();
      } else {
        window.location.href = 'index.html';
      }
    });
  }

  render() {
    return html`
      <div class="bg-gray-700 p-4 rounded-lg mb-8 text-gray-300">
        <h3 class="text-white text-lg font-semibold mb-2">Connect Your Gaming Accounts</h3>
        <p>Link your social and gaming accounts to participate in the KBX Tournament. These will be used to verify your identity and showcase your achievements.</p>
      </div>

      ${this.message ? html`
        <div class="p-3 rounded-lg mb-4 font-medium ${this.message.includes('Error') ? 'bg-red-900 text-red-200 border border-red-700' : 'bg-green-900 text-green-200 border border-green-700'}">
          ${this.message}
        </div>
      ` : ''}

      <form @submit=${this.handleSubmit}>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="mb-6">
            <label for="minecraft" class="block text-gray-300 text-sm font-medium mb-2">
              🎮 Minecraft Username
            </label>
            <input
              type="text"
              id="minecraft"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.minecraft}
              @input=${(e: any) => this.updateAccount('minecraft', e.target.value)}
              placeholder="Enter your Minecraft username"
            />
          </div>

          <div class="mb-6">
            <label for="discord" class="block text-gray-300 text-sm font-medium mb-2">
              💬 Discord
            </label>
            <input
              type="text"
              id="discord"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.discord}
              @input=${(e: any) => this.updateAccount('discord', e.target.value)}
              placeholder="username#1234 or @username"
            />
          </div>

          <div class="mb-6">
            <label for="youtube" class="block text-gray-300 text-sm font-medium mb-2">
              📺 YouTube
            </label>
            <input
              type="text"
              id="youtube"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.youtube}
              @input=${(e: any) => this.updateAccount('youtube', e.target.value)}
              placeholder="Channel name or URL"
            />
          </div>

          <div class="mb-6">
            <label for="twitch" class="block text-gray-300 text-sm font-medium mb-2">
              🟣 Twitch
            </label>
            <input
              type="text"
              id="twitch"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.twitch}
              @input=${(e: any) => this.updateAccount('twitch', e.target.value)}
              placeholder="Twitch username"
            />
          </div>

          <div class="mb-6">
            <label for="twitter" class="block text-gray-300 text-sm font-medium mb-2">
              🐦 X (Twitter)
            </label>
            <input
              type="text"
              id="twitter"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.twitter}
              @input=${(e: any) => this.updateAccount('twitter', e.target.value)}
              placeholder="@username"
            />
          </div>

          <div class="mb-6">
            <label for="reddit" class="block text-gray-300 text-sm font-medium mb-2">
              🔴 Reddit
            </label>
            <input
              type="text"
              id="reddit"
              class="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              .value=${this.accounts.reddit}
              @input=${(e: any) => this.updateAccount('reddit', e.target.value)}
              placeholder="u/username"
            />
          </div>
        </div>

        <button
          type="submit"
          class="py-3 px-6 bg-primary hover:bg-primary/80 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-md transition-colors"
          ?disabled=${this.loading}
        >
          ${this.loading ? 'Saving...' : 'Save Accounts'}
        </button>
      </form>
    `;
  }

  private updateAccount(platform: keyof UserAccounts, value: string) {
    this.accounts = {
      ...this.accounts,
      [platform]: value
    };
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    this.loading = true;
    this.message = '';

    try {
      const response = await fetch(`${API_ENDPOINT}/api/users/accounts`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId: this.userId,
          accounts: this.accounts
        })
      });

      if (response.ok) {
        this.message = 'Accounts saved successfully!';
        localStorage.setItem(`accounts_${this.userId}`, JSON.stringify(this.accounts));
      } else {
        throw new Error('Failed to save accounts');
      }
    } catch (error) {
      this.message = 'Error saving accounts. Please try again.';
      console.error('Error saving accounts:', error);
      // Fallback to localStorage for development
      localStorage.setItem(`accounts_${this.userId}`, JSON.stringify(this.accounts));
      this.message = 'Accounts saved locally (API unavailable)';
    } finally {
      this.loading = false;
    }
  }

  private async loadUserAccounts() {
    try {
      const response = await fetch(`${API_ENDPOINT}/api/users/${this.userId}/accounts`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.accounts = { ...this.accounts, ...data.accounts };
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem(`accounts_${this.userId}`);
        if (stored) {
          this.accounts = { ...this.accounts, ...JSON.parse(stored) };
        }
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem(`accounts_${this.userId}`);
      if (stored) {
        this.accounts = { ...this.accounts, ...JSON.parse(stored) };
      }
    }
  }

  private async getAuthToken(): Promise<string> {
    const auth = getAuth();
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    return '';
  }
}
